import React, { FC, useEffect, useState } from 'react'
import { InnerWrapper } from './style'

//* utils
import { LocaleCodes } from '../../../../../constants/localization.constants'
import { ComponentAnnouncement } from '../../../../../models/components/announcement'
import { ComponentMainNavBanner } from '../../../../../models/components/mainNavBanner'
import { MenuGroup } from '../../../../../models/others/menuGroup'
import { gatherAllYears, getMicrocopies, getPageNumber, getQueryParams } from './utils/get'
import { handleListFiltering } from './utils/handle'
import { urlFormattedTypeParam, urlFormattedYearParam } from './utils/url'

//* components
import SEOTags from '../../../../global/seoTags'
import Layout from '../../../../layouts/layout'
import { getFeaturedListing, getModules } from './subcomponents/dynamic'
import { ListingPageContent } from './subcomponents/listing'
import { LeadershipPageContent, OfficePageContent } from './subcomponents/office'

type Props = {
  pageContext: any
  location: any
  headerData?:
    | {
        allContentfulNavMenu: {
          nodes: MenuGroup[]
        }
      }
    | undefined
  footerData?:
    | {
        allContentfulNavMenu: {
          nodes: MenuGroup[]
        }
      }
    | undefined
  announcementData?: ComponentAnnouncement
  mainNavBannerData?: ComponentMainNavBanner
  browserLanguageData: any
}

const CompanyListingPage: FC<Props> = ({
  pageContext,
  location,
  headerData,
  footerData,
  announcementData,
  mainNavBannerData,
  browserLanguageData,
}) => {
  const pageContent: any = {
    hero: null,
    slug: pageContext.slug,
    locale: pageContext.pageLocale || LocaleCodes.ENGLISH.code,
    featuredListings: pageContext.featuredListings || [],
    localeListings: pageContext.localeListings || [],
    pageNumber: pageContext.pageNumber || 0,
    numberOfPages: pageContext.numberOfPages || 0,
    listingsPerPage: 6,
    listingIDsToExlude: new Set(),
  }

  const listingPageData = {
    microcopy: pageContext.companyListingPageData.microcopy || [],
    tabOrder: pageContext.companyListingPageData.tabOrder || [],
    useCase: pageContext.companyListingPageData.useCase || '',
    slug: pageContext.companyListingPageData.slug || '/',
    seoMetadata: pageContext.companyListingPageData.seoMetadata || null,
    flexSection: pageContext.companyListingPageData.flexSection || [],
    breadcrumb: pageContext.companyListingPageData.breadcrumb || null,
    name:
      pageContext.companyListingPageData.pageName || pageContext.companyListingPageData.name || '',
    metaInformation: pageContext.companyListingPageData.metaInformation || null,
  }

  const { LISTING_TYPES, yearTextParameter, typeTextParameter } = getMicrocopies(
    listingPageData.microcopy
  )

  let featuredListings = []
  const listingIDsToExlude = new Set()

  if (pageContent.featuredListings.length > 0) {
    featuredListings = getFeaturedListing(
      pageContent.featuredListings,
      pageContent.locale,
      listingIDsToExlude,
      LISTING_TYPES
    )
  }

  const dynamicPageContent = getModules(listingPageData, pageContent, location)
  const localeListings = pageContent.localeListings.filter(
    (listing: any) => !listingIDsToExlude.has(listing.id)
  )

  const [typeSelection, setTypeSelection] = useState('')
  const [yearSelection, setYearSelection] = useState<string[]>([])
  const [filteredListings, setFilteredListings] = useState(localeListings)

  const listingYears = gatherAllYears(localeListings)
  const currentPage = getPageNumber(pageContent.pageNumber)
  const start = (currentPage - 1) * pageContent.listingsPerPage
  const end = currentPage * pageContent.listingsPerPage

  const hrefForPage = (page: number) => {
    let url = ''
    let queryString = typeof window !== 'undefined' ? window.location.search.slice(1) : ''

    if (page === 1) {
      //* url = /newsroom
      if (typeof window !== 'undefined') {
        if (queryString) {
          url = `/${listingPageData.slug}?${queryString}`
        } else {
          url = `/${listingPageData.slug}`
        }
      } else if (listingPageData.slug) {
        url = `/${listingPageData.slug}`
      }
    } else {
      //* url = /newrsoom/page?[query]
      if (typeof window !== 'undefined') {
        if (queryString) {
          url = `/${listingPageData.slug}/${page}?${queryString}`
        } else {
          url = `/${listingPageData.slug}/${page}`
        }
      } else if (listingPageData.slug) {
        url = `/${listingPageData.slug}/${page}`
      }
    }

    return url
  }

  const updateURL = (yearParam: string[], typeParam: string) => {
    let query = ''
    let formattedYear = urlFormattedYearParam(yearParam)
    let formattedType = urlFormattedTypeParam(typeParam)

    //* if the type is All Articles, we don't need to add type parameter
    if (typeParam === LISTING_TYPES.ALL_ARTICLES) typeParam = ''
    if (yearParam.length && typeParam) {
      query = `${yearTextParameter}=${formattedYear}&${typeTextParameter}=${formattedType}`
    } else {
      query =
        (yearParam.length && `${yearTextParameter}=${formattedYear}`) ||
        (typeParam && `${typeTextParameter}=${formattedType}`) ||
        ''
    }

    if (typeof window !== `undefined`) {
      if (query == '') {
        history.pushState(null, '', '/' + listingPageData.slug)
      } else {
        history.pushState('', '', `/${listingPageData.slug}?${query}`)
      }
    }
  }

  //* Initial page load, set the type and year selection if any
  useEffect(() => {
    const [yearParam, typeParam] = getQueryParams(
      LISTING_TYPES,
      yearTextParameter,
      typeTextParameter
    )

    handleFilterItemClick(
      typeParam,
      //* Get array form of years from query value
      yearParam && yearParam.split(',').length ? yearParam.split(',') : [],
      false
    )
  }, [])

  const handleFilterItemClick = (
    type: string | null,
    year: string[] | null,
    wasClicked: boolean
  ) => {
    if (type == '') setTypeSelection(LISTING_TYPES.ALL_ARTICLES)
    else if (type != null) setTypeSelection(type)
    if (year != null) setYearSelection(year)
    //* wasClicked will be false if the page was loaded with the filter params, in that case we don't need to update URL
    if (wasClicked) {
      updateURL(year ? year : yearSelection, type || typeSelection)
    }
  }

  useEffect(() => {
    handleListFiltering(
      localeListings,
      typeSelection,
      LISTING_TYPES,
      yearSelection,
      setFilteredListings
    )
  }, [yearSelection, typeSelection])

  const listingPageContent = (
    <ListingPageContent
      featuredListings={featuredListings}
      handleFilterItemClick={handleFilterItemClick}
      listingPageData={listingPageData}
      typeSelection={typeSelection}
      listingYears={listingYears}
      filteredListings={filteredListings}
      start={start}
      end={end}
      pageContent={pageContent}
      hrefForPage={hrefForPage}
      yearSelection={yearSelection}
      locale={pageContent.locale}
    />
  )

  const officesPageContent = <OfficePageContent listingPageData={listingPageData} />
  const leadershipPageContent = <LeadershipPageContent listingPageData={listingPageData} />

  const pageInner = (
    <InnerWrapper data-cy={`CompanyListing`} $useCase={listingPageData.useCase}>
      <SEOTags
        seoMetadata={listingPageData.seoMetadata}
        locale={pageContent.locale}
        slug={pageContent.slug}
        path={pageContent.slug}
        isPaginated={true}
        pageNum={currentPage}
        hrefForPage={hrefForPage}
        isLastPage={currentPage === pageContent.numberOfPages}
        availableLocales={pageContext.availableLocales}
      />

      {pageContent.hero}
      {listingPageData.useCase === 'Newsroom' && listingPageContent}
      {listingPageData.useCase === 'Offices' && officesPageContent}
      {listingPageData.useCase === 'Leadership' && leadershipPageContent}
      {dynamicPageContent}
    </InnerWrapper>
  )

  return (
    <Layout.Template
      lang={pageContent.locale}
      pageType="companyListing"
      slug={listingPageData.slug}
      headerData={headerData}
      footerData={footerData}
      announcementData={announcementData}
      mainNavBannerData={mainNavBannerData}
      browserLanguageData={browserLanguageData}
      availableLocales={pageContext.availableLocales}>
      {pageInner}
    </Layout.Template>
  )
}

export default CompanyListingPage
